import React, { ReactElement, useEffect, useState } from 'react';

import styles from './yamlEditor.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import YamlEditor from './YamlEditor';
import { useApiContext } from '../../api';
import { TpTemplateInfo } from '../../api/models/assets';
import VerticalSplitter from '../primitives/VerticalSplitter';
import HorizontalSplitter from '../primitives/HorizontalSplitter';
import YamlSchemaDocumentation from './YamlSchemaDocumentation';
import { verifyYaml } from '../jsonSchemaValidator';
import { useAppDispatch } from '../../app/hooks';
import { addTemplate, modifyTemplate } from '../../data/templateSlice';
import { showErrorMessage } from '../message-boxes/ErrorMessage';

export interface TpTemplateEditoreProps {
  initialTemplate: TpTemplateInfo | string;
  contentModified: (newContent: string) => void;
  infoPanel?: ReactElement | null;
  yamlSchema?: any;
  onUpdate?: (newContent: string) => void;
  onSaveToDB?: (newContent: string) => Promise<string>;
  onTemplateCopied?: (template: TpTemplateInfo) => void;
  canSaveAsCopy?: boolean;
}

function TemplateEditor({
  initialTemplate,
  contentModified,
  infoPanel,
  yamlSchema,
  onUpdate,
  onSaveToDB,
  onTemplateCopied,
  canSaveAsCopy = true,
}: TpTemplateEditoreProps) {
  const dispatch = useAppDispatch();
  const [active, setActive] = useState(false);
  const api = useApiContext();
  const [content, setContent] = useState('');
  const [verificationError, setVerificationError] = useState<any>(null);

  useEffect(() => {
    const initialTemplateString = typeof initialTemplate === 'string' ? initialTemplate : initialTemplate.yamlString;
    setContent(initialTemplateString);
  }, [initialTemplate]);

  const validateContent = () => {
    const error = verifyYaml(content);
    setVerificationError(error);
    return !error;
  };

  const handleUpdate = () => {
    if (!validateContent()) return;

    setActive(false);
    onUpdate?.(content);
    contentModified(content);
  };

  const handleSaveToDatabase = async () => {
    if (!validateContent() || typeof initialTemplate === 'string') return;

    setActive(false);
    try {
      if (onSaveToDB) {
        const savedContent = await onSaveToDB(content);
        contentModified(savedContent);
      } else {
        const savedTemplate = await api.template.modifyTemplate(initialTemplate.id!, { yamlString: content });
        dispatch(modifyTemplate({ template: savedTemplate }));
        contentModified(savedTemplate.yamlString);
      }
    } catch (error) {
      showErrorMessage(error instanceof Error ? error.message : String(error));
    }
  };

  const handleSaveAsCopy = async () => {
    if (!validateContent() || typeof initialTemplate === 'string' || !initialTemplate.id) return;

    try {
      const copiedTemplate = await api.template.copyTemplate(initialTemplate.id, content);
      dispatch(addTemplate({ template: copiedTemplate }));
      onTemplateCopied?.(copiedTemplate);
      setActive(false);
    } catch (error) {
      showErrorMessage(error instanceof Error ? error.message : String(error));
    }
  };

  return (
    <>
      {!active && (
        <div className={styles.templateEditButton} onClick={() => setActive(true)}>
          <FontAwesomeIcon icon={['fas', 'gear']} />
        </div>
      )}
      <div
        className={styles.templateEditBackgroundBlocker}
        style={{ display: active ? '' : 'none' }}
        onClick={() => setActive(false)}
      />
      <div className={styles.templateEditWrapper} style={{ display: active ? '' : 'none' }}>
        <div className={styles.templateEditTopBar}>
          <button onClick={handleUpdate}>Update</button>
          {typeof initialTemplate !== 'string' && (
            <>
              <button onClick={handleSaveToDatabase}>Save to DB</button>
              {!onSaveToDB && canSaveAsCopy && initialTemplate.id && (
                <button onClick={handleSaveAsCopy}>Save as copy</button>
              )}
            </>
          )}
        </div>
        <div className={styles.templateEditBody}>
          <VerticalSplitter
            left={
              <div className={styles.yamlEditorWrapper}>
                <YamlEditor
                  initialContent={content}
                  contentModified={(newContent) => {
                    setContent(newContent);
                  }}
                />
              </div>
            }
            right={
              <HorizontalSplitter
                top={yamlSchema && <YamlSchemaDocumentation yamlSchema={yamlSchema} />}
                bottom={verificationError || infoPanel || null}
              />
            }
          />
        </div>
      </div>
    </>
  );
}

export default TemplateEditor;
